import { customElement } from 'lit/decorators.js';
import { html } from 'lit';
import {
  ModalPickerDialog,
  type ModalPickerDialogOptions
} from '../../../../../webmodule-common/other/ui/modal-picker-base';
import { DataTableWrapper } from '../../../../../webmodule-common/other/ui/datatable-view';
import type { RequestPage, ResultPaginated } from '../../../../../webmodule-common/other/ui/RequestPage';
import type {
  WebModuleTable,
  WebModuleTableColumnDef
} from '../../../../../webmodule-common/components/src/webmodule-components';
import type { NullPromise } from '../../../../../webmodule-common/other/ui/events';
import { v6Util, type FinishColorOption } from '../../../../../webmodule-common/interop/interfaces/v6-config';
import { LangFabricationPurchaseOrder } from '../../../../../localization/scripts/fabrication-purchase-orders/views/fabrication-purchase-order-lang';
import { fireQuickErrorToast } from '../../../../../webmodule-common/other/toast-away';

type FinishResourceType = 'extrusion' | 'component' | 'gasket';

interface FinishStepTableOptions {
  supplierId: string;
  resourceType: FinishResourceType;
  resourceRef: string;
  // Items already fetched by the caller (so the picker does not re-fetch); falls back to a lazy fetch.
  preloaded?: FinishColorOption[];
  onPick?: (c: FinishColorOption) => void;
}

@customElement('wm-postep-finishtable')
class FinishStepTable extends DataTableWrapper<FinishColorOption> {
  private opts: FinishStepTableOptions;
  private _allItems: FinishColorOption[] | null;

  constructor(opts: FinishStepTableOptions) {
    super();
    this.opts = opts;
    this._allItems = opts.preloaded ?? null;
    this.clickrows = true;
  }

  override disablePagination(): boolean {
    return true;
  }

  override isDataLoadDelayed(): boolean {
    return false;
  }

  override enableFiltering(): boolean {
    return true;
  }

  override updateFilter(searchTerm: string | null) {
    this.filterValue = searchTerm;
  }

  override async getRowsFromServer(_request: RequestPage): Promise<ResultPaginated<FinishColorOption>> {
    this._allItems ??= await v6Util().getFinishesByResource(
      this.opts.supplierId,
      this.opts.resourceType,
      this.opts.resourceRef
    );
    const filtered = this.filterValue
      ? this._allItems.filter(i => `${i.code} ${i.description}`.toLowerCase().includes(this.filterValue!.toLowerCase()))
      : this._allItems;
    return { count: filtered.length, pageCount: 1, pageIndex: 0, pageSize: filtered.length, results: filtered };
  }

  // No column is `sortable`: getRowsFromServer filters the preloaded list and returns it in
  // the order V6 supplied it, and the table does not sort client-side - so a sortable header
  // would re-fetch and return the identical order.
  override getColumns(): WebModuleTableColumnDef[] {
    return [
      {
        title: () => LangFabricationPurchaseOrder.pickerFinishCodeColumn(),
        fieldName: 'code',
        displayValue: (_t: WebModuleTable, item: unknown) => html`${(item as FinishColorOption).code}`
      },
      {
        title: () => LangFabricationPurchaseOrder.pickerDescriptionColumn(),
        fieldName: 'description',
        fade: true,
        displayValue: (_t: WebModuleTable, item: unknown) => html`${(item as FinishColorOption).description}`
      }
    ];
  }

  override async rowClicked(e: CustomEvent<{ table: WebModuleTable; item: FinishColorOption }>): Promise<void> {
    e.stopImmediatePropagation();
    e.preventDefault();
    this.eventHandler(async () => {
      this.opts.onPick?.(e.detail.item);
    });
  }
}

interface FinishStepPickerOptions extends ModalPickerDialogOptions<FinishColorOption> {
  supplierId: string;
  resourceType: FinishResourceType;
  resourceRef: string;
  preloaded?: FinishColorOption[];
}

class FinishStepPicker extends ModalPickerDialog<FinishColorOption> {
  private table: FinishStepTable;

  constructor(options: FinishStepPickerOptions) {
    super(options);
    this.table = new FinishStepTable({
      supplierId: options.supplierId,
      resourceType: options.resourceType,
      resourceRef: options.resourceRef,
      preloaded: options.preloaded,
      onPick: c => this.select(c)
    });
  }

  protected get modalSize(): string {
    return 'modal-xl';
  }

  bodyTemplate() {
    return html`${this.table}`;
  }

  footerTemplate() {
    return html`<webmodule-button size="small" variant="default" @click=${() => this.cancel()}>
      ${LangFabricationPurchaseOrder.pickerCancel()}
    </webmodule-button>`;
  }
}

export async function pickFinishColor(
  supplierId: string,
  resourceType: FinishResourceType,
  resourceRef: string,
  title: string
): NullPromise<FinishColorOption> {
  const all = await v6Util().getFinishesByResource(supplierId, resourceType, resourceRef);
  // Nothing available - toast and bail rather than open an empty modal the user can only cancel.
  if (all.length === 0) {
    fireQuickErrorToast(LangFabricationPurchaseOrder.noFinishesAvailable());
    return null;
  }
  // Nothing to choose when there is a single finish - auto-select it and skip the picker.
  if (all.length === 1) return all[0];
  return new Promise<FinishColorOption | null>(resolve => {
    const picker = new FinishStepPicker({
      onSelect: c => resolve(c),
      onCancel: () => resolve(null),
      title,
      supplierId,
      resourceType,
      resourceRef,
      preloaded: all
    });
    picker.showModal().then(() => resolve(null));
  });
}
